import { useState, useEffect, useRef } from "react";
import { UserName } from "@/components/badges/UserName";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useThreadMessages, ThreadMessage } from "@/hooks/useMessageThreads";
import { useTypingIndicator } from "@/hooks/useTypingIndicator";
import {
  useMentorshipRequestByThread,
} from "@/hooks/useMentorshipMeetings";
import {
  useScheduledMeeting,
  useOpenProposalsWithSlots,
} from "@/hooks/useScheduledMeetings";
import {
  usePendingImmediateMeeting,
  useAcceptedImmediateMeeting,
  useCanRequestImmediateMeeting,
} from "@/hooks/useImmediateMeetings";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Send,
  Loader2,
  MoreVertical,
  Pencil,
  Trash2,
  CalendarClock,
  CheckCircle2,
  BookOpen,
  Info,
  Lock,
  Video,
} from "lucide-react";
import { format, differenceInMinutes } from "date-fns";
import { toast } from "sonner";
import { ScheduleMeetingDialog } from "./ScheduleMeetingDialog";
import { RescheduleMeetingDialog } from "./RescheduleMeetingDialog";
import { SuggestMeetingDialog } from "./SuggestMeetingDialog";
import { RequestMeetingDialog } from "./RequestMeetingDialog";
import { CompleteMentorshipDialog } from "./CompleteMentorshipDialog";
import { MentorshipReflectionDialog } from "./MentorshipReflectionDialog";
import { ScheduledMeetingCard } from "./ScheduledMeetingCard";
import { ImmediateMeetingCard } from "./ImmediateMeetingCard";

interface MentorshipThreadPanelProps {
  threadId: string;
  otherPerson: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  onBack?: () => void;
  autoOpenSchedule?: boolean;
}

const EDIT_WINDOW_MINUTES = 15;

export function MentorshipThreadPanel({
  threadId,
  otherPerson,
  onBack,
  autoOpenSchedule,
}: MentorshipThreadPanelProps) {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const queryClient = useQueryClient();
  const { messages, isLoading, sendMessage, markAsRead, editMessage, deleteMessage } =
    useThreadMessages(threadId);
  const { typingUsers, setTyping, stopTyping } = useTypingIndicator(
    threadId,
    profile?.full_name || "User"
  );

  const { data: mentorshipRequest } = useMentorshipRequestByThread(threadId);
  const { data: scheduledMeeting } = useScheduledMeeting(threadId);
  const { data: openProposals, isLoading: openProposalsLoading } = useOpenProposalsWithSlots(threadId);
  
  // NEW: Separate hooks for immediate meetings
  const { data: pendingImmediateMeeting, isLoading: pendingImmediateLoading } = usePendingImmediateMeeting(threadId);
  const { data: acceptedImmediateMeeting } = useAcceptedImmediateMeeting(threadId);
  const { data: canRequestImmediateData } = useCanRequestImmediateMeeting(threadId);

  const [newMessage, setNewMessage] = useState("");
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<string | null>(null);

  // Dialog states
  const [scheduleMeetingOpen, setScheduleMeetingOpen] = useState(false);
  const [rescheduleMeetingOpen, setRescheduleMeetingOpen] = useState(false);
  const [suggestMeetingOpen, setSuggestMeetingOpen] = useState(false);
  const [requestMeetingOpen, setRequestMeetingOpen] = useState(false);
  const [completeMentorshipOpen, setCompleteMentorshipOpen] = useState(false);
  const [reflectionDialogOpen, setReflectionDialogOpen] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isMentor = mentorshipRequest?.mentor_id === user?.id;
  const isEntrepreneur = mentorshipRequest?.entrepreneur_id === user?.id;
  const isActive = mentorshipRequest?.status === "accepted";
  const isClosed = mentorshipRequest?.status === "closed";
  const topicName = mentorshipRequest?.topic?.name;

  // Check if entrepreneur should see reflection prompt
  const showReflectionPrompt =
    isClosed &&
    isEntrepreneur &&
    !mentorshipRequest?.entrepreneur_reflection &&
    !mentorshipRequest?.entrepreneur_thank_you;

  // Mark unread messages as read
  useEffect(() => {
    if (!user?.id || !messages.length) return;

    const unreadMessages = messages.filter(
      (m) => m.sender_id !== user.id && !m.read_at
    );

    if (unreadMessages.length > 0) {
      markAsRead.mutate(unreadMessages.map((m) => m.id));
    }
  }, [messages, user?.id]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Force refresh of pending meeting query on mount to avoid stale data race conditions
  useEffect(() => {
    queryClient.invalidateQueries({ 
      queryKey: ["pending-meeting-proposal", threadId] 
    });
    queryClient.invalidateQueries({ 
      queryKey: ["open-proposals-with-slots", threadId] 
    });
  }, [threadId, queryClient]);

  // Auto-open schedule dialog when navigated from connection card Schedule button
  useEffect(() => {
    if (autoOpenSchedule && isActive) {
      setScheduleMeetingOpen(true);
    }
  }, [autoOpenSchedule, isActive]);

  useEffect(() => {
    if (!threadId) return;

    const channel = supabase
      .channel(`mentorship-meeting-updates-${threadId}`)
      // Scheduled meetings (slot-based)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'mentorship_meetings',
          filter: `thread_id=eq.${threadId}`,
        },
        (payload) => {
          console.log('[Realtime] Scheduled meeting update:', payload);
          queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", threadId] });
          queryClient.invalidateQueries({ queryKey: ["mentorship-meetings", threadId] });
          queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", threadId] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'mentorship_meeting_proposals',
        },
        (payload) => {
          console.log('[Realtime] Proposal update:', payload);
          queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", threadId] });
          queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", threadId] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'mentorship_meeting_proposal_slots',
        },
        (payload) => {
          console.log('[Realtime] Proposal slots update:', payload);
          // CRITICAL: Clear stale cache before invalidating to ensure fresh data
          queryClient.setQueryData(["open-proposals-with-slots", threadId], undefined);
          queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", threadId] });
        }
      )
      // Immediate "Zoom Now" meetings (separate table)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'immediate_meetings',
          filter: `thread_id=eq.${threadId}`,
        },
        (payload) => {
          console.log('[Realtime] Immediate meeting update:', payload);
          queryClient.invalidateQueries({ queryKey: ["pending-immediate-meeting", threadId] });
          queryClient.invalidateQueries({ queryKey: ["accepted-immediate-meeting", threadId] });
          queryClient.invalidateQueries({ queryKey: ["can-request-immediate-meeting", threadId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [threadId, queryClient]);

  // Track if meeting card queries have resolved (for loading guard)
  const meetingCardsLoading = openProposalsLoading || pendingImmediateLoading;

  const handleSend = async () => {
    if (!newMessage.trim() || !threadId || isClosed) return;

    try {
      stopTyping();
      await sendMessage.mutateAsync({ threadId, content: newMessage.trim() });
      setNewMessage("");
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    setTyping(true);

    typingTimeoutRef.current = setTimeout(() => {
      setTyping(false);
    }, 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canEditMessage = (message: ThreadMessage) => {
    if (message.sender_id !== user?.id) return false;
    const minutesSinceSent = differenceInMinutes(
      new Date(),
      new Date(message.created_at)
    );
    return minutesSinceSent <= EDIT_WINDOW_MINUTES;
  };

  const handleStartEdit = (message: ThreadMessage) => {
    setEditingMessageId(message.id);
    setEditContent(message.content);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent("");
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId || !editContent.trim()) return;

    try {
      await editMessage.mutateAsync({
        messageId: editingMessageId,
        content: editContent.trim(),
      });
      setEditingMessageId(null);
      setEditContent("");
      toast.success("Message updated");
    } catch (error) {
      toast.error("Failed to update message");
    }
  };

  const handleDeleteClick = (messageId: string) => {
    setMessageToDelete(messageId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!messageToDelete) return;

    try {
      await deleteMessage.mutateAsync(messageToDelete);
      toast.success("Message deleted");
    } catch (error) {
      toast.error("Failed to delete message");
    } finally {
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
    }
  };

  const otherTyping = typingUsers.filter((u) => u.id !== user?.id);

  // Check if there's an active scheduled meeting
  const hasScheduledMeeting = scheduledMeeting?.status === "scheduled";
  const hasPendingProposals = openProposals && openProposals.length > 0;
  const hasActiveScheduledMeeting = hasScheduledMeeting || hasPendingProposals;
  
  // NEW: Check for immediate meetings using new table
  const hasPendingImmediateMeeting = !!pendingImmediateMeeting;
  const hasAcceptedImmediateMeeting = !!acceptedImmediateMeeting;
  
  // Both mentor and entrepreneur can schedule/view meetings when active
  const canAccessScheduleMeeting = isActive;
  
  // Can request/suggest immediate meeting if no pending proposal exists
  const canRequestImmediateMeeting = isActive && canRequestImmediateData?.canRequest;

  return (
    <Card className="h-[calc(100vh-250px)] min-h-[400px] max-h-[700px] flex flex-col">
      <CardHeader className="border-b py-3 px-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <UserAvatar
            avatarPath={otherPerson.avatar_url}
            fullName={otherPerson.full_name}
            className="h-10 w-10"
          />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg"><UserName userId={otherPerson.id} name={otherPerson.full_name} /></CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="text-xs">
                <BookOpen className="h-3 w-3 mr-1" />
                {topicName || "Mentorship"}
              </Badge>
              {isActive && (
                <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                  Active
                </Badge>
              )}
              {isClosed && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  <Lock className="h-3 w-3 mr-1" />
                  Completed
                </Badge>
              )}
            </div>
          </div>

          {/* Mentor Actions */}
          {isMentor && isActive && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* Immediate Zoom meeting option */}
                <DropdownMenuItem 
                  onClick={() => setSuggestMeetingOpen(true)}
                  disabled={!canRequestImmediateMeeting}
                >
                  <Video className="h-4 w-4 mr-2" />
                  Suggest Zoom Now
                </DropdownMenuItem>
                
                {/* Scheduled meeting option - always accessible when active */}
                <DropdownMenuItem 
                  onClick={() => setScheduleMeetingOpen(true)}
                  disabled={!canAccessScheduleMeeting}
                >
                  <CalendarClock className="h-4 w-4 mr-2" />
                  {hasActiveScheduledMeeting ? "View Scheduled Meeting" : "Schedule Meeting"}
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setCompleteMentorshipOpen(true)}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark Complete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Entrepreneur Actions */}
          {isEntrepreneur && isActive && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* Request immediate Zoom meeting */}
                <DropdownMenuItem 
                  onClick={() => setRequestMeetingOpen(true)}
                  disabled={!canRequestImmediateMeeting}
                >
                  <Video className="h-4 w-4 mr-2" />
                  Request Zoom Now
                </DropdownMenuItem>
                
                {/* Schedule meeting - always accessible when active */}
                <DropdownMenuItem 
                  onClick={() => setScheduleMeetingOpen(true)}
                  disabled={!canAccessScheduleMeeting}
                >
                  <CalendarClock className="h-4 w-4 mr-2" />
                  {hasActiveScheduledMeeting ? "View Scheduled Meeting" : "Schedule Meeting"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        {/* Mentorship context banner */}
        <div className="px-4 py-2 bg-muted/30 text-xs text-muted-foreground border-b flex justify-between items-center">
          <div>
            <Info className="h-3 w-3 inline mr-1" />
            This mentorship is designed for focused, short-term guidance. Meetings are
            optional and at the mentor's discretion.
          </div>
          <span className="text-[10px] opacity-50 font-mono">v2026.01.12.3</span>
        </div>

        {/* Reflection prompt for completed mentorship */}
        {showReflectionPrompt && (
          <div className="px-4 py-3 bg-pink-50 dark:bg-pink-900/20 border-b">
            <Alert className="border-pink-200 dark:border-pink-800">
              <AlertDescription className="flex items-center justify-between gap-4">
                <span>
                  This mentorship is complete. Would you like to share your thoughts?
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setReflectionDialogOpen(true)}
                >
                  Share Thoughts
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Meeting Cards Section - only render after queries have resolved */}
        {!meetingCardsLoading && (
          <>
            {/* Pending Immediate Meeting Card - from new immediate_meetings table */}
            {pendingImmediateMeeting && mentorshipRequest && (
              <div className="px-4 py-3 border-b">
                <ImmediateMeetingCard
                  meeting={pendingImmediateMeeting}
                  mentorshipRequestId={mentorshipRequest.id}
                  proposerName={pendingImmediateMeeting.proposed_by === mentorshipRequest.mentor_id 
                    ? (mentorshipRequest.mentor?.full_name || "Mentor") 
                    : (mentorshipRequest.entrepreneur?.full_name || "Entrepreneur")}
                  isProposer={pendingImmediateMeeting.proposed_by === user?.id}
                />
              </div>
            )}

            {/* Accepted Immediate Meeting Card - from new immediate_meetings table */}
            {acceptedImmediateMeeting && mentorshipRequest && !pendingImmediateMeeting && (
              <div className="px-4 py-3 border-b">
                <ImmediateMeetingCard
                  meeting={acceptedImmediateMeeting}
                  mentorshipRequestId={mentorshipRequest.id}
                  proposerName={acceptedImmediateMeeting.proposed_by === mentorshipRequest.mentor_id 
                    ? (mentorshipRequest.mentor?.full_name || "Mentor") 
                    : (mentorshipRequest.entrepreneur?.full_name || "Entrepreneur")}
                  isProposer={acceptedImmediateMeeting.proposed_by === user?.id}
                />
              </div>
            )}

            {/* Scheduled/Proposed Meeting Card (slot-based) - completely separate */}
            {mentorshipRequest && (scheduledMeeting || (openProposals && openProposals.length > 0)) && (
              <div className="px-4 py-3 border-b">
                <ScheduledMeetingCard
                  meeting={scheduledMeeting || null}
                  proposals={openProposals || []}
                  mentorshipRequest={mentorshipRequest}
                  isMentor={isMentor}
                  onReschedule={() => setRescheduleMeetingOpen(true)}
                  threadId={threadId}
                />
              </div>
            )}
          </>
        )}

        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <p>No messages yet.</p>
              <p className="text-sm mt-1">Start the conversation!</p>
            </div>
          ) : (
            <div className="space-y-1">
              {messages.map((message, index) => {
                const isOwn = message.sender_id === user?.id;
                const sender = Array.isArray(message.sender)
                  ? message.sender[0]
                  : message.sender;
                
                // Check if this is first message in a consecutive group from same sender
                const prevMessage = index > 0 ? messages[index - 1] : null;
                const isFirstInGroup = !prevMessage || prevMessage.sender_id !== message.sender_id;

                return (
                  <div
                    key={message.id}
                    className={`flex gap-2 ${isOwn ? "justify-end" : "justify-start"} ${isFirstInGroup ? "mt-3" : "mt-0.5"}`}
                  >
                    {!isOwn && (
                      <div className="w-8 shrink-0">
                        {isFirstInGroup && (
                          <UserAvatar
                            avatarPath={sender?.avatar_url}
                            fullName={sender?.full_name}
                            className="h-8 w-8"
                            fallbackClassName="bg-muted text-muted-foreground"
                            thumbnailSize={64}
                          />
                        )}
                      </div>
                    )}
                    <div className={`max-w-[70%] ${isOwn ? "order-first" : ""}`}>
                      {editingMessageId === message.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="min-h-[60px]"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleSaveEdit}>
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="group relative">
                          <div
                            className={`rounded-lg px-3 py-2 ${
                              isOwn
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted"
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap break-words">
                              {message.content}
                            </p>
                          </div>
                          <div
                            className={`flex items-center gap-1 mt-1 ${
                              isOwn ? "justify-end" : "justify-start"
                            }`}
                          >
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(message.created_at), "h:mm a")}
                            </span>
                            {isOwn && canEditMessage(message) && !isClosed && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <MoreVertical className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => handleStartEdit(message)}
                                  >
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteClick(message.id)}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {isOwn && (
                      <div className="w-8 shrink-0">
                        {isFirstInGroup && (
                          <UserAvatar
                            avatarPath={profile?.avatar_url}
                            fullName={profile?.full_name}
                            className="h-8 w-8"
                            thumbnailSize={64}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Typing indicator */}
        {otherTyping.length > 0 && (
          <div className="px-4 py-1 text-sm text-muted-foreground">
            {otherTyping.map((u) => u.name).join(", ")}{" "}
            {otherTyping.length === 1 ? "is" : "are"} typing...
          </div>
        )}

        {/* Message input */}
        {isClosed ? (
          <div className="p-4 border-t bg-muted/30 text-center text-sm text-muted-foreground">
            <Lock className="h-4 w-4 inline mr-1" />
            This mentorship has been completed. Messages are read-only.
          </div>
        ) : (
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Textarea
                placeholder="Type a message..."
                value={newMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className="min-h-[44px] max-h-[120px] resize-none"
                rows={1}
              />
              <Button
                onClick={handleSend}
                disabled={!newMessage.trim() || sendMessage.isPending}
                className="shrink-0"
              >
                {sendMessage.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Delete message dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete message?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The message will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Schedule Meeting Dialog (for both mentor and entrepreneur) */}
      {mentorshipRequest && (
        <ScheduleMeetingDialog
          open={scheduleMeetingOpen}
          onOpenChange={setScheduleMeetingOpen}
          mentorshipRequestId={mentorshipRequest.id}
          threadId={threadId}
          otherPersonName={otherPerson.full_name}
        />
      )}

      {/* Reschedule Meeting Dialog */}
      {mentorshipRequest && scheduledMeeting && (
        <RescheduleMeetingDialog
          open={rescheduleMeetingOpen}
          onOpenChange={setRescheduleMeetingOpen}
          meetingId={scheduledMeeting.id}
          threadId={threadId}
          otherPersonName={otherPerson.full_name}
          currentScheduledTime={scheduledMeeting.scheduled_start_at}
        />
      )}

      {/* Suggest Meeting Dialog (mentor only - immediate Zoom) */}
      {mentorshipRequest && isMentor && (
        <SuggestMeetingDialog
          open={suggestMeetingOpen}
          onOpenChange={setSuggestMeetingOpen}
          mentorshipRequestId={mentorshipRequest.id}
          threadId={threadId}
          entrepreneurName={mentorshipRequest.entrepreneur?.full_name || "Entrepreneur"}
        />
      )}

      {/* Request Meeting Dialog (entrepreneur only - immediate Zoom) */}
      {mentorshipRequest && isEntrepreneur && (
        <RequestMeetingDialog
          open={requestMeetingOpen}
          onOpenChange={setRequestMeetingOpen}
          mentorshipRequestId={mentorshipRequest.id}
          threadId={threadId}
          mentorName={mentorshipRequest.mentor?.full_name || "Mentor"}
        />
      )}

      {/* Complete Mentorship Dialog (mentor only) */}
      {mentorshipRequest && mentorshipRequest.thread_id && (
        <CompleteMentorshipDialog
          open={completeMentorshipOpen}
          onOpenChange={setCompleteMentorshipOpen}
          threadId={mentorshipRequest.thread_id}
          otherPersonName={mentorshipRequest.entrepreneur?.full_name || ""}
        />
      )}

      {/* Reflection Dialog (entrepreneur only) */}
      {mentorshipRequest && isEntrepreneur && (
        <MentorshipReflectionDialog
          open={reflectionDialogOpen}
          onOpenChange={setReflectionDialogOpen}
          requestId={mentorshipRequest.id}
          mentorName={mentorshipRequest.mentor?.full_name || ""}
        />
      )}
    </Card>
  );
}
